import { RANKING_STRATEGIES } from '@/src/constants/shared/ranking-strategy';
import { ConfirmModal } from '@/src/components/common/ConfirmModal';
import { useEventRules } from '@/src/hooks/store/useEventRules';
import { JamEventDetails } from '@/src/types/JamEventDetails';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { formatEventDuration } from '@/src/utils/time.utils';
import { Alert, Badge, Box, ColumnLayout, Header, SpaceBetween } from '@amzn/awsui-components-react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';

export interface EventRulesModalProps {
  event: JamEventDetails;
}

export const EventRulesModal = NiceModal.create((props: EventRulesModalProps) => {
  const { t } = useTranslation();
  const modal = useModal();
  const eventRules = useEventRules();

  const { event } = props;

  const onConfirm = () => {
    eventRules.setAccepted(props.event.eventName, true);
    NiceModal.hide(EventRulesModal);
  };

  const eventTitle = event?.title || t(i18nKeys.events.rules.defaultEventTitle);
  const eventDuration = formatEventDuration(t, event);
  const challengeCount = event?.challenges?.length || 0;
  const eventType = event?.type || 'JAM';

  const rulesContent = (
    <SpaceBetween direction="vertical" size="l">
      <Box>
        <Header variant="h3">
          {t(i18nKeys.events.rules.welcomeTitle, { eventTitle })}
          {eventType !== 'JAM' && <Badge color="blue">{eventType}</Badge>}
        </Header>
        <p>{t(i18nKeys.events.rules.welcomeDescription)}</p>
      </Box>

      <ColumnLayout columns={2} variant="text-grid">
        <div>
          <Header variant="h3">{t(i18nKeys.events.rules.eventConfiguration.title)}</Header>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <strong>{t(i18nKeys.events.rules.eventConfiguration.duration)}:</strong> {eventDuration}
            </div>
            <div>
              <strong>{t(i18nKeys.events.rules.eventConfiguration.challenges)}:</strong>{' '}
              {challengeCount > 0
                ? t(i18nKeys.events.rules.challengesCount, { count: challengeCount })
                : t(i18nKeys.events.rules.multipleChallenges)}
            </div>
          </SpaceBetween>
        </div>

        <div>
          <Header variant="h3">{t(i18nKeys.events.rules.scoring.title)}</Header>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <strong>{t(i18nKeys.events.rules.scoring.rankingStrategy)}:</strong>{' '}
              {event?.rankingStrategy === RANKING_STRATEGIES.MOST_RECENT_SCORE_TIME
                ? t(i18nKeys.events.rules.scoring.rankingDescriptionMostRecent)
                : t(i18nKeys.events.rules.scoring.rankingDescription)}
            </div>
            <div>
              <strong>{t(i18nKeys.events.rules.scoring.pointValues)}:</strong>{' '}
              {t(i18nKeys.events.rules.scoring.pointValuesDescription)}
            </div>
            <div>
              <strong>{t(i18nKeys.events.rules.scoring.cluePenalties)}:</strong>{' '}
              {t(i18nKeys.events.rules.scoring.cluePenaltiesDescription)}
            </div>
          </SpaceBetween>
        </div>
      </ColumnLayout>

      <Box>
        <Header variant="h3">{t(i18nKeys.events.rules.guidelines.title)}</Header>
        <ColumnLayout columns={2}>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <strong>🤝 {t(i18nKeys.events.rules.guidelines.collaboration.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.collaboration.description)}
            </div>
            <div>
              <strong>⏱️ {t(i18nKeys.events.rules.guidelines.timeManagement.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.timeManagement.description)}
            </div>
            <div>
              <strong>💡 {t(i18nKeys.events.rules.guidelines.strategicThinking.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.strategicThinking.description)}
            </div>
          </SpaceBetween>
          <SpaceBetween direction="vertical" size="s">
            <div>
              <strong>🎯 {t(i18nKeys.events.rules.guidelines.focusOnLearning.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.focusOnLearning.description)}
            </div>
            <div>
              <strong>🤖 {t(i18nKeys.events.rules.guidelines.fairPlay.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.fairPlay.description)}
            </div>
            <div>
              <strong>🔧 {t(i18nKeys.events.rules.guidelines.resourceManagement.title)}:</strong>{' '}
              {t(i18nKeys.events.rules.guidelines.resourceManagement.description)}
            </div>
          </SpaceBetween>
        </ColumnLayout>
      </Box>

      {event?.supportChatEnabled && (
        <Box>
          <Header variant="h3">{t(i18nKeys.events.rules.support.title)}</Header>
          <div>
            <Badge color="green">{t(i18nKeys.events.rules.support.chatEnabled)}</Badge> -{' '}
            {t(i18nKeys.events.rules.support.description)}
          </div>
        </Box>
      )}

      <Box padding={{ top: 'm' }} color="text-status-info">
        <Alert>
          <strong>{t(i18nKeys.events.rules.acknowledgment.title)}:</strong>{' '}
          {t(i18nKeys.events.rules.acknowledgment.description)}
        </Alert>
      </Box>
    </SpaceBetween>
  );

  return (
    <ConfirmModal
      onConfirm={onConfirm}
      onDismiss={onConfirm}
      confirmBtnLabel={t(i18nKeys.events.rules.acceptButton)}
      title={t(i18nKeys.events.rules.modalTitle)}
      visible={modal.visible}
      size="large"
      message={rulesContent}
      data-testid="event-rules-modal"
    />
  );
});
